import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { parseUTCTimestamp } from '../utils/dateUtils';
import { 
  CalendarIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  PlusIcon, 
  CheckIcon,
  SunIcon,
  MoonIcon,
  PencilIcon,
  ClockIcon,
  HeartIcon,
  ListBulletIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { Shuffle, Edit2, Plus, X } from 'lucide-react';
import { useDays } from '../hooks/useDays';
import { useWeeks } from '../hooks/useWeeks';
import { promptsAPI, api, journalAPI } from '../services/api';
import type { JournalEntryCreate } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { Notification } from './Notification';
import { Modal } from './Modal';
import { DaySchedule } from './DaySchedule';
import { EventModal } from './EventModal';
import TodoForm from './TodoForm';
import TodoItem from './TodoItem';
import { SearchFilterBar } from './SearchFilterBar';
import { TaskReviewModal } from './TaskReviewModal';
import { DaySummary } from './DaySummary';
import type { Todo, Event, TodoCreate, TodoUpdate, TaskStatus, ReviewEntry } from '../types';
import { DEFAULT_PROMPT_SETS } from '../data/prompts';

interface MoodEnergyProps {
  mood?: string;
  energy?: string;
  onMoodChange: (mood: string) => void;
  onEnergyChange: (energy: string) => void;
  disabled?: boolean;
}

const MoodEnergySelector: React.FC<MoodEnergyProps> = ({ 
  mood, 
  energy, 
  onMoodChange, 
  onEnergyChange, 
  disabled = false 
}) => {
  const moodOptions = [
    { value: 'excellent', label: '😊 Excellent', color: 'text-green-600' },
    { value: 'good', label: '🙂 Good', color: 'text-blue-600' },
    { value: 'okay', label: '😐 Okay', color: 'text-yellow-600' },
    { value: 'challenging', label: '😕 Challenging', color: 'text-orange-600' },
    { value: 'difficult', label: '😞 Difficult', color: 'text-red-600' }
  ];

  const energyOptions = [
    { value: 'high', label: '⚡ High', color: 'text-green-600' },
    { value: 'medium', label: '🔋 Medium', color: 'text-yellow-600' },
    { value: 'low', label: '🪫 Low', color: 'text-red-600' }
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How is your mood?
        </label>
        <select
          value={mood || ''}
          onChange={(e) => onMoodChange(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select mood...</option>
          {moodOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How is your energy?
        </label>
        <select
          value={energy || ''}
          onChange={(e) => onEnergyChange(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select energy...</option>
          {energyOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

interface DayViewProps {
  onTaskClick?: (task: Todo) => void;
  onTodoStatusChange?: (todoId: string, status: TaskStatus) => void;
  todos: Todo[];
  selectedTeamId?: string | null;
  teamMembers?: { id: string; name: string; email: string; }[];
  isPersonalMode?: boolean;
  currentUser?: { id: string; name: string; email: string; };
  allTags?: string[];
  onUpdateTodo?: (data: TodoCreate) => void;
  onCancelTodoModal?: () => void;
  onDeleteTodo?: (todoId: string) => void;
  onCloneTodo?: (todoId: string) => void;
  onStartPomodoro?: (todo: Todo) => void;
  onAddTodayTask?: () => void;
  onMakeChild?: (childId: string, parentId: string) => void;
}

export const DayView: React.FC<DayViewProps> = ({ 
  onTaskClick, 
  onTodoStatusChange,
  todos,
  // selectedTeamId not destructured - no longer used after project removal
  teamMembers = [],
  isPersonalMode = true,
  currentUser,
  allTags = [],
  onUpdateTodo,
  // onCancelTodoModal, // Keeping for potential future use
  onDeleteTodo,
  onCloneTodo,
  onStartPomodoro,
  onAddTodayTask,
  onMakeChild
}) => {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showStartDayModal, setShowStartDayModal] = useState(false);
  const [showEndDayModal, setShowEndDayModal] = useState(false);
  const [showGeneralJournalModal, setShowGeneralJournalModal] = useState(false);
  const [showAddTasksModal, setShowAddTasksModal] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showWeekDetails, setShowWeekDetails] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventModalStartTime, setEventModalStartTime] = useState<Date | undefined>(undefined);
  const [showSchedule, setShowSchedule] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Todo | null>(null);
  const [modalStack, setModalStack] = useState<'event' | 'task'>('event');
  const [showTaskReviewModal, setShowTaskReviewModal] = useState(false);
  const [endDayStep, setEndDayStep] = useState<'summary' | 'journal'>('summary');
  const [taskReviewSelectedTask, setTaskReviewSelectedTask] = useState<Todo | null>(null);
  const [taskReviewDate, setTaskReviewDate] = useState(selectedDate);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [horizonFilter, setHorizonFilter] = useState<string[]>([]);
  const [tagsFilter, setTagsFilter] = useState<string[]>([]);
  const [taskTypeFilter, setTaskTypeFilter] = useState<'all' | 'parent_only' | 'child_only'>('all');
  const [isTodayTasksExpanded, setIsTodayTasksExpanded] = useState(true);
  const [hoveredAddButton, setHoveredAddButton] = useState<string | null>(null);
  const [showIncompleteTodayTasksOnly, setShowIncompleteTodayTasksOnly] = useState(false);
  
  const {
    currentDay,
    completedTasks,
    updateDay,
    startDay,
    endDay,
    isLoading,
    error
  } = useDays(selectedDate);
  
  // Get current week data
  const { currentWeek } = useWeeks(selectedDate);

  // Backward compatibility helper: convert old Record<string, string> to ReviewEntry[]
  const ensureReviewEntries = (data: ReviewEntry[] | Record<string, string> | null | undefined, defaultCategory?: string): ReviewEntry[] => {
    if (!data) {
      if (defaultCategory && DEFAULT_PROMPT_SETS[defaultCategory as keyof typeof DEFAULT_PROMPT_SETS]) {
        return DEFAULT_PROMPT_SETS[defaultCategory as keyof typeof DEFAULT_PROMPT_SETS].map(p => ({ prompt: p, response: '' }));
      }
      return [];
    }
    if (Array.isArray(data)) return data;
    // Convert old Record<string, string> format
    return Object.entries(data).map(([key, value]) => ({ prompt: key, response: value as string }));
  };

  // Use filtered todos passed as props instead of fetching all todos

  // Filter handlers
  const onStatusToggle = (status: string) => {
    setStatusFilter(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const onHorizonToggle = (horizon: string) => {
    setHorizonFilter(prev =>
      prev.includes(horizon) ? prev.filter(h => h !== horizon) : [...prev, horizon]
    );
  };

  const onTagToggle = (tag: string) => {
    setTagsFilter(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const onClearFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setHorizonFilter([]);
    setTagsFilter([]);
    setTaskTypeFilter('all');
  };

  // Get today's tasks with filtering
  const todaysTasks = useMemo(() => {
    if (!todos) return [];
    
    const isToday = selectedDate.toDateString() === new Date().toDateString();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0);
    
    return todos.filter(todo => {
      // Show tasks with horizon=NOW, or tasks with deadline on/before selected date
      let matchesDay = false;

      // Horizon NOW tasks always show in day view
      if (todo.horizon === 'now') {
        matchesDay = true;
      }

      // Also show tasks with deadline on/before selected date
      if (!matchesDay && todo.deadline) {
        const deadlineDate = new Date(todo.deadline.split('T')[0]);
        deadlineDate.setHours(0, 0, 0, 0);
        if (isToday) {
          matchesDay = deadlineDate <= today;
        } else {
          matchesDay = deadlineDate.getTime() === selectedDateObj.getTime();
        }
      }

      if (!matchesDay) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchableText = `${todo.title} ${todo.description || ''} ${todo.tags?.join(' ') || ''}`.toLowerCase();
        if (!searchableText.includes(query)) return false;
      }

      // Status filter
      if (statusFilter.length > 0 && !statusFilter.includes(todo.status)) {
        return false;
      }

      // Horizon filter
      if (horizonFilter.length > 0 && !horizonFilter.includes(todo.horizon || '')) {
        return false;
      }

      // Tags filter
      if (tagsFilter.length > 0 && (!todo.tags || !todo.tags.some(tag => tagsFilter.includes(tag)))) {
        return false;
      }

      // Task type filter
      if (taskTypeFilter === 'parent_only' && todo.parent_id) {
        return false;
      }
      if (taskTypeFilter === 'child_only' && !todo.parent_id) {
        return false;
      }

      // Incomplete only filter for Today's Tasks
      if (showIncompleteTodayTasksOnly && (todo.status === 'completed' || todo.status === 'canceled')) {
        return false;
      }

      return true;
    });
  }, [todos, selectedDate, searchQuery, statusFilter, horizonFilter, tagsFilter, taskTypeFilter, showIncompleteTodayTasksOnly]);

  // Get tasks created today
  const tasksCreatedToday = useMemo(() => {
    if (!todos) return [];
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    
    return todos.filter(todo => {
      if (!todo.created_time) return false;
      const createdDateStr = todo.created_time.split('T')[0];
      return createdDateStr === selectedDateStr;
    });
  }, [todos, selectedDate]);

  // Start of day form state
  const [startDayData, setStartDayData] = useState({
    morning_mood: '',
    morning_energy: '',
    start_day_reflections: [] as ReviewEntry[],
    priorities_for_day: [] as string[]
  });

  // End of day form state
  const [endDayData, setEndDayData] = useState({
    evening_mood: '',
    evening_energy: '',
    end_day_reflections: [] as ReviewEntry[],
    went_well: [] as string[],
    could_improve: [] as string[],
    remember_tomorrow: [] as string[]
  });

  // General journal form state
  const [generalJournalData, setGeneralJournalData] = useState({
    general_reflections: [] as ReviewEntry[],
    refresh_notes: '',
    current_mood: '',
    current_energy: ''
  });

  // State for inline prompt editing
  const [editingStartIndex, setEditingStartIndex] = useState<number | null>(null);
  const [editingEndIndex, setEditingEndIndex] = useState<number | null>(null);
  const [editingGeneralIndex, setEditingGeneralIndex] = useState<number | null>(null);

  // Create chronological timeline of day activities
  const dayTimeline = useMemo(() => {
    const timeline = [];
    
    // Add day start event
    if (currentDay?.is_day_started && currentDay.day_started_time) {
      timeline.push({
        time: parseUTCTimestamp(currentDay.day_started_time),
        type: 'day_start',
        title: 'Day Started',
        mood: currentDay.morning_mood,
        energy: currentDay.morning_energy,
        icon: SunIcon,
        color: 'text-yellow-500'
      });
    }

    // Add task creations for today
    if (todaysTasks) {
      todaysTasks.forEach(task => {
        if (task.created_time) {
          const createdDate = parseUTCTimestamp(task.created_time);
          const today = new Date(selectedDate);
          if (createdDate.toDateString() === today.toDateString()) {
            timeline.push({
              time: createdDate,
              type: 'task_created',
              title: `Created: ${task.title}`,
              description: task.description,
              icon: PlusIcon,
              color: 'text-blue-600'
            });
          }
        }
      });
    }

    // Add completed tasks
    if (completedTasks) {
      completedTasks.forEach(task => {
        if (task.completed_date) {
          timeline.push({
            time: parseUTCTimestamp(task.completed_date),
            type: 'task_completed',
            title: `Completed: ${task.title}`,
            description: task.description,
            icon: CheckIcon,
            color: 'text-green-600'
          });
        }
      });
    }

    // Add mood log entries
    if (currentDay?.mood_logs) {
      currentDay.mood_logs.forEach(entry => {
        if (entry.timestamp) {
          timeline.push({
            time: parseUTCTimestamp(entry.timestamp),
            type: 'mood_log',
            title: 'Mood & Energy Check-in',
            mood: entry.mood,
            energy: entry.energy,
            icon: HeartIcon,
            color: 'text-pink-500'
          });
        }
      });
    }

    // Add day end event
    if (currentDay?.is_day_ended && currentDay.day_ended_time) {
      timeline.push({
        time: parseUTCTimestamp(currentDay.day_ended_time),
        type: 'day_end',
        title: 'Day Ended',
        mood: currentDay.evening_mood,
        energy: currentDay.evening_energy,
        icon: MoonIcon,
        color: 'text-purple-500'
      });
    }

    // Sort by time
    return timeline.sort((a, b) => a.time.getTime() - b.time.getTime());
  }, [currentDay, completedTasks, todaysTasks, selectedDate]);

  // Create journal reflection log
  const journalLog = useMemo(() => {
    const log = [];

    // Add start day reflections
    if (currentDay?.start_day_reflections && currentDay.day_started_time) {
      const entries = ensureReviewEntries(currentDay.start_day_reflections);
      entries.forEach((entry: ReviewEntry) => {
        if (entry.response.trim()) {
          log.push({
            time: parseUTCTimestamp(currentDay.day_started_time!),
            type: 'start_reflection',
            prompt: entry.prompt,
            response: entry.response,
            icon: SunIcon,
            color: 'text-yellow-500'
          });
        }
      });
    }

    // Add end day reflections
    if (currentDay?.end_day_reflections && currentDay.day_ended_time) {
      const entries = ensureReviewEntries(currentDay.end_day_reflections);
      entries.forEach((entry: ReviewEntry) => {
        if (entry.response.trim()) {
          log.push({
            time: parseUTCTimestamp(currentDay.day_ended_time!),
            type: 'end_reflection',
            prompt: entry.prompt,
            response: entry.response,
            icon: MoonIcon,
            color: 'text-purple-500'
          });
        }
      });
    }

    // Add general reflections (use current day's updated time)
    if (currentDay?.general_reflections && currentDay.updated_time) {
      const entries = ensureReviewEntries(currentDay.general_reflections);
      entries.forEach((entry: ReviewEntry) => {
        if (entry.response.trim()) {
          log.push({
            time: parseUTCTimestamp(currentDay.updated_time),
            type: 'general_reflection',
            prompt: entry.prompt,
            response: entry.response,
            icon: PencilIcon,
            color: 'text-blue-500'
          });
        }
      });
    }

    return log.sort((a, b) => a.time.getTime() - b.time.getTime());
  }, [currentDay]);

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  const openStartDayModal = () => {
    if (currentDay?.id) {
      const existingEntries = ensureReviewEntries(currentDay.start_day_reflections, 'start_day');
      const entries = existingEntries.length > 0
        ? existingEntries
        : DEFAULT_PROMPT_SETS.start_day.map(p => ({ prompt: p, response: '' }));

      setStartDayData({
        morning_mood: currentDay.morning_mood || '',
        morning_energy: currentDay.morning_energy || '',
        start_day_reflections: entries,
        priorities_for_day: currentDay.priorities_for_day || []
      });

      setEditingStartIndex(null);
      setShowStartDayModal(true);
    }
  };

  const openEndDayModal = () => {
    if (currentDay?.id) {
      const existingEntries = ensureReviewEntries(currentDay.end_day_reflections, 'end_day');
      const entries = existingEntries.length > 0
        ? existingEntries
        : DEFAULT_PROMPT_SETS.end_day.map(p => ({ prompt: p, response: '' }));

      setEndDayData({
        evening_mood: currentDay.evening_mood || '',
        evening_energy: currentDay.evening_energy || '',
        end_day_reflections: entries,
        went_well: currentDay.went_well || [],
        could_improve: currentDay.could_improve || [],
        remember_tomorrow: currentDay.remember_tomorrow || []
      });

      setEditingEndIndex(null);
      setShowEndDayModal(true);
    }
  };

  const openGeneralJournalModal = () => {
    if (currentDay?.id) {
      // Start fresh for new reflections with default prompts
      const entries = DEFAULT_PROMPT_SETS.general.map(p => ({ prompt: p, response: '' }));

      setGeneralJournalData({
        general_reflections: entries,
        refresh_notes: '',
        current_mood: '',
        current_energy: ''
      });

      setEditingGeneralIndex(null);
      setShowGeneralJournalModal(true);
    }
  };

  const handleRandomPrompt = async (index: number, type: 'start' | 'end' | 'general') => {
    try {
      const { prompt: randomPrompt } = await promptsAPI.getInspirationRandom('daily');

      if (type === 'start') {
        setStartDayData(prev => {
          const updated = [...prev.start_day_reflections];
          updated[index] = { ...updated[index], prompt: randomPrompt };
          return { ...prev, start_day_reflections: updated };
        });
      } else if (type === 'end') {
        setEndDayData(prev => {
          const updated = [...prev.end_day_reflections];
          updated[index] = { ...updated[index], prompt: randomPrompt };
          return { ...prev, end_day_reflections: updated };
        });
      } else {
        setGeneralJournalData(prev => {
          const updated = [...prev.general_reflections];
          updated[index] = { ...updated[index], prompt: randomPrompt };
          return { ...prev, general_reflections: updated };
        });
      }
    } catch (err) {
      console.error('Failed to get random prompt:', err);
      setNotification({ message: 'Failed to get random prompt', type: 'error' });
    }
  };

  const handleStartDay = async () => {
    if (!currentDay?.id) return;

    try {
      await startDay(currentDay.id, startDayData);
      setShowStartDayModal(false);
      setNotification({ message: 'Day started successfully!', type: 'success' });

      // Fire-and-forget journal entries
      const dateLabel = format(selectedDate, 'MMMM d, yyyy');
      const journalEntries: JournalEntryCreate[] = startDayData.start_day_reflections
        .filter(e => e.response.trim())
        .map(e => ({
          content: e.response,
          prompt: e.prompt,
          source: 'daily_start' as const,
          period_type: 'day',
          period_id: currentDay.id,
          period_label: dateLabel,
          mood: startDayData.morning_mood || undefined,
          energy: startDayData.morning_energy || undefined,
        }));
      if (journalEntries.length > 0) {
        journalAPI.createBulk(journalEntries).catch(console.error);
      }
    } catch (err) {
      console.error('Failed to start day:', err);
      setNotification({ message: 'Failed to start day', type: 'error' });
    }
  };

  const handleEndDay = async () => {
    if (!currentDay?.id) return;

    if (endDayStep === 'journal') {
      // Save journal data and close
      try {
        await endDay(currentDay.id, endDayData);
        setShowEndDayModal(false);
        setEndDayStep('summary'); // Reset for next time
        setNotification({ message: 'Day ended successfully!', type: 'success' });

        // Fire-and-forget journal entries
        const dateLabel = format(selectedDate, 'MMMM d, yyyy');
        const journalEntries: JournalEntryCreate[] = endDayData.end_day_reflections
          .filter(e => e.response.trim())
          .map(e => ({
            content: e.response,
            prompt: e.prompt,
            source: 'daily_end' as const,
            period_type: 'day',
            period_id: currentDay.id,
            period_label: dateLabel,
            mood: endDayData.evening_mood || undefined,
            energy: endDayData.evening_energy || undefined,
          }));
        if (journalEntries.length > 0) {
          journalAPI.createBulk(journalEntries).catch(console.error);
        }
      } catch (err) {
        console.error('Failed to end day:', err);
        setNotification({ message: 'Failed to end day', type: 'error' });
      }
    }
  };

  const handleSaveGeneralJournal = async () => {
    if (!currentDay?.id) return;

    try {
      await updateDay(currentDay.id, generalJournalData);
      setShowGeneralJournalModal(false);
      setNotification({ message: 'Journal saved successfully!', type: 'success' });

      // Fire-and-forget journal entries
      const dateLabel = format(selectedDate, 'MMMM d, yyyy');
      const journalEntries: JournalEntryCreate[] = generalJournalData.general_reflections
        .filter(e => e.response.trim())
        .map(e => ({
          content: e.response,
          prompt: e.prompt,
          source: 'daily_general' as const,
          period_type: 'day',
          period_id: currentDay.id,
          period_label: dateLabel,
          mood: generalJournalData.current_mood || undefined,
          energy: generalJournalData.current_energy || undefined,
        }));
      if (journalEntries.length > 0) {
        journalAPI.createBulk(journalEntries).catch(console.error);
      }
    } catch (err) {
      console.error('Failed to save journal:', err);
      setNotification({ message: 'Failed to save journal', type: 'error' });
    }
  };

  const handleAddTasksToDay = async (taskIds: string[]) => {
    if (taskIds.length === 0) return;

    try {
      // Use the API directly to update each task's horizon to NOW
      const updatePromises = taskIds.map(taskId =>
        api.put(`/todos/${taskId}`, { horizon: 'now' })
      );
      
      await Promise.all(updatePromises);
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      
      setShowAddTasksModal(false);
      setSelectedTaskIds([]);
      setNotification({ message: `${taskIds.length} task(s) set to today successfully!`, type: 'success' });
    } catch (err) {
      console.error('Failed to set tasks to today:', err);
      setNotification({ message: 'Failed to set tasks to today', type: 'error' });
    }
  };

  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleTimeBlockClick = (startTime: Date) => {
    setSelectedEvent(null);
    setEventModalStartTime(startTime);
    setShowEventModal(true);
  };

  const handleEventModalClose = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setEventModalStartTime(undefined);
    setModalStack('event');
  };

  const handleLinkedTodoClick = (todo: Todo) => {
    setSelectedTask(todo);
    setModalStack('task');
    // Keep event modal open in background
  };

  const handleTaskModalClose = () => {
    setSelectedTask(null);
    setModalStack('event');
    // Return focus to event modal
  };

  const handleTaskUpdate = (data: TodoCreate) => {
    if (onUpdateTodo) {
      onUpdateTodo(data);
    }
    handleTaskModalClose();
  };

  const handleTaskClickInReview = (task: Todo) => {
    setTaskReviewSelectedTask(task);
  };

  const handleTaskModalCloseInReview = () => {
    setTaskReviewSelectedTask(null);
  };

  const handleTaskDrop = async (taskId: string, dropDate: Date, timeSlot?: Date) => {
    const task = todos.find(t => t.id === taskId);
    if (!task) {
      return;
    }

    try {
      // Set the task's horizon to NOW
      const updateData: TodoUpdate = {
        horizon: 'now'
      };
      
      // Update the task via API
      await api.put(`/todos/${taskId}`, updateData);
      
      // Refetch todos to update the UI
      await queryClient.invalidateQueries({ queryKey: ['todos'] });

      // If dropped on a time slot, create an event
      if (timeSlot) {
        const endTime = new Date(timeSlot);
        endTime.setMinutes(endTime.getMinutes() + 30); // 30-minute default duration
        
        // Create event via API directly
        await api.post('/events/', {
          title: task.title,
          description: task.description,
          start_time: format(timeSlot, "yyyy-MM-dd'T'HH:mm:ss"),
          end_time: format(endTime, "yyyy-MM-dd'T'HH:mm:ss"),
          todo_id: task.id,
          color: '#3B82F6'
        });
        
        // Refetch events to update the calendar
        await queryClient.invalidateQueries({ queryKey: ['events'] });
      }
      
      setNotification({ message: 'Task scheduled successfully!', type: 'success' });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to schedule task:', error);
      }
      setNotification({ message: 'Failed to schedule task', type: 'error' });
    }
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const formattedDate = format(selectedDate, 'EEEE, MMMM d, yyyy');

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600">Error loading day data: {error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateDay('prev')}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <h1 className="text-2xl font-bold text-gray-900">
              {formattedDate}
            </h1>
            {isToday && (
              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                Today
              </span>
            )}
          </div>

          <button
            onClick={() => navigateDay('next')}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => setShowSchedule(!showSchedule)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md ${
              showSchedule ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-600'
            } hover:bg-gray-300`}
          >
            <CalendarDaysIcon className="h-4 w-4" />
            <span>Schedule</span>
          </button>

          {isToday && (
            <button
              onClick={openStartDayModal}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
            >
              <SunIcon className="h-4 w-4" />
              <span>{currentDay?.is_day_started ? 'Edit Day Kickoff' : 'Start Day'}</span>
            </button>
          )}

          {isToday && (
            <button
              onClick={openEndDayModal}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              <MoonIcon className="h-4 w-4" />
              <span>{currentDay?.is_day_ended ? 'Edit Day Wrap-up' : 'End Day'}</span>
            </button>
          )}

          <button
            onClick={openGeneralJournalModal}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PencilIcon className="h-4 w-4" />
            <span>Journal</span>
          </button>

          <button
            onClick={() => {
              setTaskReviewDate(selectedDate);
              setShowTaskReviewModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <ListBulletIcon className="h-4 w-4" />
            <span>Review/Add Tasks</span>
          </button>

        </div>
      </div>

      {/* Filter Bar */}
      <div className="mb-4">
        <SearchFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          statusFilter={statusFilter}
          onStatusToggle={onStatusToggle}
          horizonFilter={horizonFilter}
          onHorizonToggle={onHorizonToggle}
          tagsFilter={tagsFilter}
          onTagToggle={onTagToggle}
          taskTypeFilter={taskTypeFilter}
          onTaskTypeChange={setTaskTypeFilter}
          allTags={allTags}
          onClearFilters={onClearFilters}
        />
      </div>

      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      )}

      {!isLoading && (
        <div className="flex gap-6">
          {/* Left side - Schedule (if visible) */}
          {showSchedule && (
            <div className="w-1/3 min-w-[400px]">
              <div className="h-[600px]">
                <DaySchedule
                  date={selectedDate}
                  onEventClick={handleEventClick}
                  onTimeBlockClick={handleTimeBlockClick}
                  onTaskDrop={(taskId, dropTime) => handleTaskDrop(taskId, selectedDate, dropTime)}
                />
              </div>
            </div>
          )}
          
          {/* Right side - Content */}
          <div className="flex-1 flex gap-6">
            {/* Tasks Column - Takes most space */}
            <div className="flex-1">
              {/* Today's Tasks */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg">
                  <button
                    type="button"
                    onClick={() => setIsTodayTasksExpanded(!isTodayTasksExpanded)}
                    className="flex-1 text-left"
                    title="Tasks with deadline or todo date of today or in the past"
                  >
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      {isTodayTasksExpanded ? (
                        <ChevronDownIcon className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                      ) : (
                        <ChevronUpIcon className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                      )}
                      <ClockIcon className="w-5 h-5 mr-2 text-blue-600" />
                      Today's Tasks
                      <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                        ({todaysTasks.length})
                      </span>
                    </h2>
                  </button>
                  
                  <div className="flex items-center gap-2 ml-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddTasksModal(true);
                      }}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all duration-200"
                      title="Select existing tasks to add to today"
                    >
                      Add Tasks
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onAddTodayTask) {
                          onAddTodayTask();
                        }
                      }}
                      onMouseEnter={() => setHoveredAddButton('today')}
                      onMouseLeave={() => setHoveredAddButton(null)}
                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                      title="Create new task due today"
                    >
                      {hoveredAddButton === 'today' ? (
                        <X className="w-4 h-4 transform rotate-45 transition-transform duration-200" />
                      ) : (
                        <Plus className="w-4 h-4 transition-transform duration-200" />
                      )}
                    </button>
                  </div>
                </div>
                
                {isTodayTasksExpanded && (
                  <div className="px-3 pb-3">
                    <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>
                    
                    {/* Toggle for completed tasks */}
                    <div className="flex items-center justify-end mb-4">
                      <span className="text-xs text-gray-600 dark:text-gray-400 mr-2">
                        {showIncompleteTodayTasksOnly ? 'Incomplete Only' : 'All Tasks'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowIncompleteTodayTasksOnly(!showIncompleteTodayTasksOnly)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          showIncompleteTodayTasksOnly
                            ? 'bg-blue-600' 
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                        title={showIncompleteTodayTasksOnly ? 'Show all tasks' : 'Show incomplete tasks only'}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            showIncompleteTodayTasksOnly ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    {todaysTasks.length === 0 ? (
                      <div className="text-center py-8">
                        <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">No tasks for today</p>
                        <button 
                          type="button" 
                          onClick={() => setShowAddTasksModal(true)} 
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Add Task
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {todaysTasks.map(todo => (
                          <TodoItem
                            key={todo.id}
                            todo={todo}
                            onClick={() => onTaskClick?.(todo)}
                            onStatusChange={(status) => {
                              onTodoStatusChange?.(todo.id, status);
                            }}
                            onSubtaskStatusChange={() => {
                              // Subtask status changes handled by parent component
                            }}
                            onAddChild={() => {
                              // Child adding handled by parent component
                            }}
                            onSubtaskClick={onTaskClick}
                            onMakeChild={onMakeChild}
                            onStartPomodoro={onStartPomodoro}
                            onDelete={onDeleteTodo}
                            allTodos={todos}
                            teamMembers={teamMembers || []}
                            isPersonalMode={isPersonalMode}
                            isDraggable={true}

                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Sidebar - Day Status and Activity */}
            <div className="w-72 xl:w-80 space-y-6">
              {/* Day Status */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Day Status</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Day Started</span>
                    <div className="flex items-center space-x-2">
                      {currentDay?.is_day_started ? (
                        <>
                          <CheckIcon className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-600">
                            {currentDay.day_started_time && format(parseUTCTimestamp(currentDay.day_started_time), 'h:mm a')}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">Not started</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Day Ended</span>
                    <div className="flex items-center space-x-2">
                      {currentDay?.is_day_ended ? (
                        <>
                          <CheckIcon className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-600">
                            {currentDay.day_ended_time && format(parseUTCTimestamp(currentDay.day_ended_time), 'h:mm a')}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">Not ended</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Tasks Completed</span>
                    <span className="text-lg font-bold text-blue-600">
                      {completedTasks?.length || 0}
                    </span>
                  </div>
                </div>
              </div>

            {/* Mood & Energy Log */}
            {((currentDay?.morning_mood || currentDay?.evening_mood) || (currentDay?.mood_logs && currentDay.mood_logs.length > 0)) && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Mood & Energy Log</h3>
                <div className="space-y-3">
                  {currentDay.morning_mood && (
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-md">
                      <div className="flex items-center space-x-3">
                        <SunIcon className="h-5 w-5 text-yellow-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Morning Check-in</p>
                          <div className="text-sm text-gray-600">
                            Mood: {currentDay.morning_mood}
                            {currentDay.morning_energy && ` • Energy: ${currentDay.morning_energy}`}
                          </div>
                        </div>
                      </div>
                      {currentDay.day_started_time && (
                        <p className="text-xs text-gray-500">
                          {format(parseUTCTimestamp(currentDay.day_started_time), 'h:mm a')}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* General mood logs */}
                  {currentDay.mood_logs && currentDay.mood_logs.length > 0 && currentDay.mood_logs.map((log, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
                      <div className="flex items-center space-x-3">
                        <HeartIcon className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">General Check-in</p>
                          <div className="text-sm text-gray-600">
                            {log.mood && `Mood: ${log.mood}`}
                            {log.mood && log.energy && ' • '}
                            {log.energy && `Energy: ${log.energy}`}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {format(parseUTCTimestamp(log.timestamp), 'h:mm a')}
                      </p>
                    </div>
                  ))}
                  
                  {currentDay.evening_mood && (
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-md">
                      <div className="flex items-center space-x-3">
                        <MoonIcon className="h-5 w-5 text-purple-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Evening Check-in</p>
                          <div className="text-sm text-gray-600">
                            Mood: {currentDay.evening_mood}
                            {currentDay.evening_energy && ` • Energy: ${currentDay.evening_energy}`}
                          </div>
                        </div>
                      </div>
                      {currentDay.day_ended_time && (
                        <p className="text-xs text-gray-500">
                          {format(parseUTCTimestamp(currentDay.day_ended_time), 'h:mm a')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Completed Tasks Summary */}
            {completedTasks && completedTasks.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Completed Tasks ({completedTasks.length})</h3>
                <div className="space-y-2">
                  {completedTasks.map((task) => (
                    <div key={task.id} onClick={() => onTaskClick?.(task)} className="border rounded-md p-3 bg-green-50 cursor-pointer hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-grow">
                          <h4 className="font-medium text-gray-900 line-through">
                            {task.title}
                          </h4>
                          {task.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {task.description}
                            </p>
                          )}
                          {task.completed_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              Completed at {format(parseUTCTimestamp(task.completed_date), 'h:mm a')}
                            </p>
                          )}
                        </div>
                        <CheckIcon className="h-5 w-5 text-green-600 flex-shrink-0 ml-2" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Priorities for Today */}
            {currentDay?.priorities_for_day && currentDay.priorities_for_day.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-3">Today's Priorities</h3>
                <ul className="space-y-2">
                  {currentDay.priorities_for_day.map((priority, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="flex-shrink-0 w-2 h-2 bg-orange-500 rounded-full mt-2"></span>
                      <span className="text-gray-700">{priority}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Day Timeline */}
            {dayTimeline.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <ChartBarIcon className="h-5 w-5 text-gray-500" />
                  <h3 className="text-lg font-semibold">Day Timeline</h3>
                </div>
                <div className="space-y-3">
                  {dayTimeline.map((event, index) => {
                    const Icon = event.icon;
                    return (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <Icon className={`h-5 w-5 ${event.color}`} />
                        </div>
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {event.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {format(event.time, 'h:mm a')}
                            </p>
                          </div>
                          {event.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {event.description}
                            </p>
                          )}
                          {(event.mood || event.energy) && (
                            <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                              {event.mood && <span>Mood: {event.mood}</span>}
                              {event.energy && <span>Energy: {event.energy}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Journal Reflection Log */}
            {journalLog.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Journal Reflection Log</h3>
                <div className="space-y-4">
                  {journalLog.map((entry, index) => {
                    const Icon = entry.icon;
                    return (
                      <div key={index} className="border-l-2 border-gray-200 pl-4">
                        <div className="flex items-start space-x-2 mb-2">
                          <Icon className={`h-4 w-4 ${entry.color} flex-shrink-0 mt-0.5`} />
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-600">
                                {entry.prompt}
                              </p>
                              <p className="text-xs text-gray-500">
                                {format(entry.time, 'h:mm a')}
                              </p>
                            </div>
                            <p className="text-gray-800 mt-1 text-sm">
                              {entry.response}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Start of Day Reflections */}
            {currentDay?.start_day_reflections && ensureReviewEntries(currentDay.start_day_reflections).some(e => e.response.trim()) && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <SunIcon className="h-5 w-5 text-yellow-500" />
                  <h3 className="text-lg font-semibold">Morning Reflections</h3>
                </div>
                <div className="space-y-4">
                  {ensureReviewEntries(currentDay.start_day_reflections).map((entry, index) => (
                    entry.response.trim() ? (
                      <div key={index} className="border-l-2 border-yellow-200 pl-4">
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          {entry.prompt}
                        </p>
                        <p className="text-gray-800">{entry.response}</p>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            )}

            {/* End of Day Reflections */}
            {currentDay?.end_day_reflections && ensureReviewEntries(currentDay.end_day_reflections).some(e => e.response.trim()) && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <MoonIcon className="h-5 w-5 text-purple-500" />
                  <h3 className="text-lg font-semibold">Evening Reflections</h3>
                </div>
                <div className="space-y-4">
                  {ensureReviewEntries(currentDay.end_day_reflections).map((entry, index) => (
                    entry.response.trim() ? (
                      <div key={index} className="border-l-2 border-purple-200 pl-4">
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          {entry.prompt}
                        </p>
                        <p className="text-gray-800">{entry.response}</p>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            )}

            {/* What Went Well / Could Improve */}
            {((currentDay?.went_well && currentDay.went_well.length > 0) || (currentDay?.could_improve && currentDay.could_improve.length > 0)) && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Daily Review</h3>
                
                {currentDay.went_well && currentDay.went_well.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-green-700 mb-2">What went well</h4>
                    <ul className="space-y-1">
                      {currentDay.went_well.map((item, index) => (
                        <li key={index} className="text-sm text-gray-700 flex items-start space-x-2">
                          <span className="flex-shrink-0 w-1.5 h-1.5 bg-green-500 rounded-full mt-2"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {currentDay.could_improve && currentDay.could_improve.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-yellow-700 mb-2">Could improve</h4>
                    <ul className="space-y-1">
                      {currentDay.could_improve.map((item, index) => (
                        <li key={index} className="text-sm text-gray-700 flex items-start space-x-2">
                          <span className="flex-shrink-0 w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* General Journal Notes */}
            {currentDay?.refresh_notes && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-3">Journal Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{currentDay.refresh_notes}</p>
              </div>
            )}

            {/* Remember for Tomorrow */}
            {currentDay?.remember_tomorrow && currentDay.remember_tomorrow.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <ClockIcon className="h-5 w-5 text-blue-500" />
                  <h3 className="text-lg font-semibold">Remember for Tomorrow</h3>
                </div>
                <ul className="space-y-2">
                  {currentDay.remember_tomorrow.map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Start Day Modal */}
      <Modal isOpen={showStartDayModal} onClose={() => {
        setShowStartDayModal(false);
      }} title="Start Your Day" size="lg">
        <div className="p-6 max-w-2xl mx-auto">
            <div className="flex items-center space-x-2 mb-6">
              <SunIcon className="h-6 w-6 text-yellow-500" />
              <h2 className="text-xl font-bold">Good Morning! Let's start your day</h2>
            </div>

          <div className="space-y-6">
            {/* Week Details Expandable Section */}
            <div className="border border-gray-200 rounded-lg">
              <button
                onClick={() => setShowWeekDetails(!showWeekDetails)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                type="button"
              >
                <div className="flex items-center space-x-2">
                  <CalendarIcon className="h-5 w-5 text-gray-500" />
                  <span className="font-medium text-gray-700">Week Details</span>
                </div>
                {showWeekDetails ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              
              {showWeekDetails && currentWeek && (
                <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                  {/* Weekly Goals */}
                  {currentWeek.goals && currentWeek.goals.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Weekly Goals</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {currentWeek.goals.map((goal, index) => (
                          <li key={index} className="text-sm text-gray-600">{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Weekly Reflections */}
                  {currentWeek.reflections && Array.isArray(currentWeek.reflections) && currentWeek.reflections.filter((e: ReviewEntry) => e.response.trim()).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Weekly Reflections</h4>
                      <div className="space-y-2">
                        {currentWeek.reflections
                          .filter((entry: ReviewEntry) => entry.response.trim())
                          .map((entry: ReviewEntry, index: number) => (
                            <div key={index}>
                              <span className="text-sm font-medium text-gray-600">
                                {entry.prompt}:
                              </span>
                              <p className="text-sm text-gray-600 mt-1">{entry.response}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* If no week data */}
                  {(!currentWeek.goals || currentWeek.goals.length === 0) &&
                   (!currentWeek.reflections || !Array.isArray(currentWeek.reflections) || currentWeek.reflections.filter((e: ReviewEntry) => e.response.trim()).length === 0) && (
                    <p className="text-sm text-gray-500 italic">No weekly review data available yet.</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Mood & Energy */}
            <MoodEnergySelector
              mood={startDayData.morning_mood}
              energy={startDayData.morning_energy}
              onMoodChange={(mood) => setStartDayData(prev => ({ ...prev, morning_mood: mood }))}
              onEnergyChange={(energy) => setStartDayData(prev => ({ ...prev, morning_energy: energy }))}
            />

            {/* Start Day Prompts */}
            {startDayData.start_day_reflections.map((entry, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  {editingStartIndex === index ? (
                    <input
                      type="text"
                      value={entry.prompt}
                      onChange={(e) => setStartDayData(prev => {
                        const updated = [...prev.start_day_reflections];
                        updated[index] = { ...updated[index], prompt: e.target.value };
                        return { ...prev, start_day_reflections: updated };
                      })}
                      onBlur={() => setEditingStartIndex(null)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setEditingStartIndex(null);
                        }
                      }}
                      className="text-sm font-medium text-gray-700 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1 mr-2"
                      autoFocus
                    />
                  ) : (
                    <label className="block text-sm font-medium text-gray-700 flex-1">
                      {entry.prompt}
                    </label>
                  )}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRandomPrompt(index, 'start')}
                      className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Get random prompt"
                      type="button"
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingStartIndex(index)}
                      className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Edit prompt"
                      type="button"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <textarea
                  value={entry.response}
                  onChange={(e) => setStartDayData(prev => {
                    const updated = [...prev.start_day_reflections];
                    updated[index] = { ...updated[index], response: e.target.value };
                    return { ...prev, start_day_reflections: updated };
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Write your thoughts..."
                />
              </div>
            ))}

            {/* Action Buttons */}
            <div className="flex justify-end pt-4 border-t">
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowStartDayModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartDay}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
                >
                  Start Day
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* End Day Modal */}
      <Modal isOpen={showEndDayModal} onClose={() => {
        setShowEndDayModal(false);
        setEndDayStep('summary');
      }} title="End Your Day" size="lg">
        {endDayStep === 'summary' ? (
          <DaySummary
            date={selectedDate}
            morningReflections={currentDay?.start_day_reflections
              ? ensureReviewEntries(currentDay.start_day_reflections)
              : undefined}
            morningMood={currentDay?.morning_mood}
            morningEnergy={currentDay?.morning_energy}
            tasksCompleted={completedTasks || []}
            tasksCreated={tasksCreatedToday}
            dayStartedTime={currentDay?.day_started_time}
            onTaskClick={onTaskClick}
            onNext={() => setEndDayStep('journal')}
          />
        ) : (
          <div className="p-6 max-w-2xl mx-auto">
            <div className="flex items-center space-x-2 mb-6">
              <MoonIcon className="h-6 w-6 text-purple-500" />
              <h2 className="text-xl font-bold">Good Evening! Let's reflect on your day</h2>
            </div>

          <div className="space-y-6">
            {/* Mood & Energy */}
            <MoodEnergySelector
              mood={endDayData.evening_mood}
              energy={endDayData.evening_energy}
              onMoodChange={(mood) => setEndDayData(prev => ({ ...prev, evening_mood: mood }))}
              onEnergyChange={(energy) => setEndDayData(prev => ({ ...prev, evening_energy: energy }))}
            />

            {/* End Day Prompts */}
            {endDayData.end_day_reflections.map((entry, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  {editingEndIndex === index ? (
                    <input
                      type="text"
                      value={entry.prompt}
                      onChange={(e) => setEndDayData(prev => {
                        const updated = [...prev.end_day_reflections];
                        updated[index] = { ...updated[index], prompt: e.target.value };
                        return { ...prev, end_day_reflections: updated };
                      })}
                      onBlur={() => setEditingEndIndex(null)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setEditingEndIndex(null);
                        }
                      }}
                      className="text-sm font-medium text-gray-700 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1 mr-2"
                      autoFocus
                    />
                  ) : (
                    <label className="block text-sm font-medium text-gray-700 flex-1">
                      {entry.prompt}
                    </label>
                  )}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRandomPrompt(index, 'end')}
                      className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Get random prompt"
                      type="button"
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingEndIndex(index)}
                      className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Edit prompt"
                      type="button"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <textarea
                  value={entry.response}
                  onChange={(e) => setEndDayData(prev => {
                    const updated = [...prev.end_day_reflections];
                    updated[index] = { ...updated[index], response: e.target.value };
                    return { ...prev, end_day_reflections: updated };
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Write your thoughts..."
                />
              </div>
            ))}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setShowEndDayModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEndDay}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                End Day
              </button>
            </div>
          </div>
        </div>
        )}
      </Modal>

      {/* General Journal Modal */}
      <Modal isOpen={showGeneralJournalModal} onClose={() => setShowGeneralJournalModal(false)} title="Journal Entry" size="lg">
        <div className="p-6 max-w-2xl mx-auto">
          <div className="flex items-center space-x-2 mb-6">
            <PencilIcon className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-bold">Journal Entry</h2>
          </div>

          <div className="space-y-6">
            {/* General Prompts */}
            {generalJournalData.general_reflections.map((entry, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  {editingGeneralIndex === index ? (
                    <input
                      type="text"
                      value={entry.prompt}
                      onChange={(e) => setGeneralJournalData(prev => {
                        const updated = [...prev.general_reflections];
                        updated[index] = { ...updated[index], prompt: e.target.value };
                        return { ...prev, general_reflections: updated };
                      })}
                      onBlur={() => setEditingGeneralIndex(null)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setEditingGeneralIndex(null);
                        }
                      }}
                      className="text-sm font-medium text-gray-700 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1 mr-2"
                      autoFocus
                    />
                  ) : (
                    <label className="block text-sm font-medium text-gray-700 flex-1">
                      {entry.prompt}
                    </label>
                  )}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRandomPrompt(index, 'general')}
                      className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Get random prompt"
                      type="button"
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingGeneralIndex(index)}
                      className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Edit prompt"
                      type="button"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <textarea
                  value={entry.response}
                  onChange={(e) => setGeneralJournalData(prev => {
                    const updated = [...prev.general_reflections];
                    updated[index] = { ...updated[index], response: e.target.value };
                    return { ...prev, general_reflections: updated };
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Write your thoughts..."
                />
              </div>
            ))}

            {/* Current Mood & Energy Check-in */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How are you feeling right now?
              </label>
              <MoodEnergySelector
                mood={generalJournalData.current_mood}
                energy={generalJournalData.current_energy}
                onMoodChange={(mood) => setGeneralJournalData(prev => ({ ...prev, current_mood: mood }))}
                onEnergyChange={(energy) => setGeneralJournalData(prev => ({ ...prev, current_energy: energy }))}
              />
            </div>

            {/* Free-form Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={generalJournalData.refresh_notes}
                onChange={(e) => setGeneralJournalData(prev => ({ ...prev, refresh_notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                placeholder="Any additional thoughts, observations, or notes..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setShowGeneralJournalModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGeneralJournal}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Journal
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Add Tasks Modal */}
      <Modal isOpen={showAddTasksModal} onClose={() => {
        setShowAddTasksModal(false);
        setSelectedTaskIds([]);
      }} title="Add Tasks to Day">
        <div className="p-6 max-w-2xl mx-auto">
          <h2 className="text-xl font-bold mb-4">Select tasks to add to today</h2>
          
          {/* Show available tasks */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {todos && todos.filter(todo => todo.status !== 'completed' && !todaysTasks.some(t => t.id === todo.id)).map((todo) => (
              <label key={todo.id} className={`flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                selectedTaskIds.includes(todo.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedTaskIds.includes(todo.id)}
                  onChange={() => toggleTaskSelection(todo.id)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                />
                <div className="flex-grow">
                  <div className={`font-medium text-gray-900 ${
                    todo.title.length > 30 ? 'text-xs' : 
                    todo.title.length > 20 ? 'text-sm' : 
                    todo.title.length > 10 ? 'text-base' :
                    'text-lg'
                  }`}>{todo.title}</div>
                  {todo.description && (
                    <div className="text-sm text-gray-600 mt-1">{todo.description}</div>
                  )}
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <span className={`px-2 py-1 rounded-full ${
                      todo.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                      todo.status === 'waiting' ? 'bg-orange-100 text-orange-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {todo.status.replace('_', ' ')}
                    </span>
                    {todo.horizon && (
                      <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                        {todo.horizon}
                      </span>
                    )}
                    {todo.deadline && (
                      <span className="text-gray-500">
                        Deadline: {new Date(todo.deadline).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="flex justify-between items-center pt-4 border-t mt-4">
            <div className="text-sm text-gray-600">
              {selectedTaskIds.length} task{selectedTaskIds.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAddTasksModal(false);
                  setSelectedTaskIds([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAddTasksToDay(selectedTaskIds)}
                disabled={selectedTaskIds.length === 0}
                className={`px-4 py-2 rounded-md font-medium ${
                  selectedTaskIds.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Add {selectedTaskIds.length > 0 ? `${selectedTaskIds.length} ` : ''}Task{selectedTaskIds.length !== 1 ? 's' : ''} to Today
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Event Modal */}
      <EventModal
        isOpen={showEventModal && modalStack === 'event'}
        onClose={handleEventModalClose}
        event={selectedEvent}
        initialStartTime={eventModalStartTime}
        initialEndTime={eventModalStartTime ? new Date(eventModalStartTime.getTime() + 60 * 60 * 1000) : undefined}
        onLinkedTodoClick={handleLinkedTodoClick}
      />

      {/* Task Modal - overlays on top when viewing linked task */}
      {selectedTask && modalStack === 'task' && (
        <Modal
          isOpen={true}
          onClose={handleTaskModalClose}
          title="Edit Task"
          size="xl"
        >
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Editing task linked to event:</span> {selectedEvent?.title}
            </p>
          </div>
          <TodoForm
            todo={selectedTask}

            availableTags={allTags}
            allTodos={todos}
            currentUser={currentUser}
            teamMembers={teamMembers}
            isPersonalMode={isPersonalMode}
            onSubmit={handleTaskUpdate}
            onCancel={handleTaskModalClose}
            onDelete={onDeleteTodo}
            onClone={onCloneTodo}
          />
        </Modal>
      )}

      {/* Task Review Modal */}
      <TaskReviewModal
        isOpen={showTaskReviewModal && !taskReviewSelectedTask}
        onClose={() => setShowTaskReviewModal(false)}
        todos={todos}
        selectedDate={taskReviewDate}
        onTaskDrop={handleTaskDrop}
        onEventClick={handleEventClick}
        onTimeBlockClick={handleTimeBlockClick}
        onTaskClick={handleTaskClickInReview}
        onDateChange={(date) => setTaskReviewDate(date)}
      />

      {/* Task Modal from Task Review */}
      {taskReviewSelectedTask && (
        <Modal
          isOpen={true}
          onClose={handleTaskModalCloseInReview}
          title="Edit Task"
          size="xl"
        >
          <TodoForm
            todo={taskReviewSelectedTask}

            availableTags={allTags}
            allTodos={todos}
            currentUser={currentUser}
            teamMembers={teamMembers}
            isPersonalMode={isPersonalMode}
            onSubmit={(data) => {
              if (onUpdateTodo) {
                onUpdateTodo(data);
              }
              handleTaskModalCloseInReview();
            }}
            onCancel={handleTaskModalCloseInReview}
            onDelete={onDeleteTodo}
            onClone={onCloneTodo}
          />
        </Modal>
      )}

      {/* Notifications */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};